import { useEffect } from 'react';

interface FacebookPixelProps {
  pixelId?: string; // Single pixel (legacy)
  pixelIds?: string[]; // Multiple pixels
}

declare global {
  interface Window {
    fbq: (...args: any[]) => void;
    _fbq: any;
  }
}

const FacebookPixel: React.FC<FacebookPixelProps> = ({ pixelId, pixelIds }) => {
  useEffect(() => {
    // Support both single pixel and multiple pixels
    const pixels = pixelIds || (pixelId ? [pixelId] : []);
    if (pixels.length === 0 || pixels.some(id => id === 'YOUR_FACEBOOK_PIXEL_ID')) return;
    if (typeof window === 'undefined' || typeof document === 'undefined') return;

    // Facebook Pixel Code - Initialize all pixels
    const script = document.createElement('script');
    script.innerHTML = `
      !function(f,b,e,v,n,t,s)
      {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
      n.callMethod.apply(n,arguments):n.queue.push(arguments)};
      if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
      n.queue=[];t=b.createElement(e);t.async=!0;
      t.src=v;s=b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t,s)}(window, document,'script',
      'https://connect.facebook.net/en_US/fbevents.js');
      ${pixels.map(id => `fbq('init', '${id}');`).join('\n      ')}
      fbq('track', 'PageView');
    `;
    document.head.appendChild(script);

    // Add noscript fallback for all pixels
    const noscript = document.createElement('noscript');
    pixels.forEach(id => {
      const img = document.createElement('img');
      img.height = 1;
      img.width = 1;
      img.style.display = 'none';
      img.src = `https://www.facebook.com/tr?id=${id}&ev=PageView&noscript=1`;
      noscript.appendChild(img);
    });
    document.head.appendChild(noscript);

    return () => {
      if (typeof document !== 'undefined') {
        document.head.removeChild(script);
        document.head.removeChild(noscript);
      }
    };
  }, [pixelId, pixelIds]);

  return null;
};

// Facebook Pixel tracking functions
export const fbTrackEvent = (eventName: string, parameters: Record<string, any> = {}) => {
  if (typeof window !== 'undefined' && window.fbq) {
    window.fbq('track', eventName, parameters);
  }
};

export const fbTrackPurchase = (value: number, currency: string = 'USD', contentIds: string[] = []) => {
  fbTrackEvent('Purchase', {
    value: value,
    currency: currency,
    content_ids: contentIds,
    content_type: 'product'
  });
};

export const fbTrackAddToCart = (value: number, currency: string = 'USD', contentId: string = '') => {
  fbTrackEvent('AddToCart', {
    value: value,
    currency: currency,
    content_ids: [contentId],
    content_type: 'product'
  });
};

export const fbTrackViewContent = (value: number, currency: string = 'USD', contentId: string = '') => {
  fbTrackEvent('ViewContent', {
    value: value,
    currency: currency,
    content_ids: [contentId],
    content_type: 'product'
  });
};

export const fbTrackInitiateCheckout = (value: number, currency: string = 'USD', contentIds: string[] = []) => {
  fbTrackEvent('InitiateCheckout', {
    value: value,
    currency: currency,
    content_ids: contentIds,
    content_type: 'product'
  });
};

export const fbTrackSearch = (searchString: string) => {
  fbTrackEvent('Search', {
    search_string: searchString
  });
};

export default FacebookPixel;